/**
 * hash 路由的地址转换（21ah-5，人工指图「打包要 hash 模式」）。纯函数，可单测；
 * 浏览器接线在 appRouter.ts。
 *
 * 为什么是 hash：产物要能丢在任何静态托管/子目录下直接跑——history 模式要求
 * 服务端把所有未知路径重写到 index.html，没有这条重写规则时刷新/深链就是 404。
 * hash 之后服务端只认一个 `index.html`，刷新与深链都不再依赖它。
 *
 * 口径：
 * - **应用内 path 一律不变**（`routes.routePath` 还是 `/chat/dm/101`）：hash 只是
 *   承载它的容器，`parseRoute`/`stackForPath` 两侧完全不用知道这件事。
 * - 写回地址时带上 `BASE_URL` 前缀并**丢掉 query**：旧口径（history 模式下
 *   `replaceState('/')`）本就会把 `?open=dm:101` 这类一次性参数清掉，
 *   冷启动跳转因此不会在刷新时重放（coldStartOpen.ts 文件头的刻意偏差），
 *   hash 模式要保持同样的行为——`'#/x'` 这种相对写法会**保留** query，所以这里
 *   必须拼出带 pathname 的完整相对 URL。
 * - 启动时没有 hash 但 pathname 不是站点根（老的 history 深链、或服务端真的把
 *   深路径重写到了 index.html）：把 pathname 当成应用内 path 收下来，老链接不死。
 */

/** hash 片段 → 应用内 path；没有 hash 返回空串。query/嵌套 hash 一律截掉 */
export function pathFromHash(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw === '') return '';
  const cut = raw.split(/[?#]/, 1)[0] ?? '';
  if (cut === '') return '';
  return cut.startsWith('/') ? cut : `/${cut}`;
}

/** 去掉站点基路径前缀（子目录部署）；不在基路径下就原样返回 */
export function stripBase(base: string, pathname: string): string {
  const prefix = base.endsWith('/') ? base.slice(0, -1) : base;
  if (prefix === '' || !pathname.startsWith(prefix)) return pathname;
  const rest = pathname.slice(prefix.length);
  return rest === '' ? '/' : rest;
}

/**
 * 启动/恢复时的应用内 path：hash 优先；没有 hash 时退回 pathname（老的 history
 * 深链），pathname 就是站点根时自然得到 `/`。
 */
export function appPathOf(base: string, pathname: string, hash: string): string {
  const fromHash = pathFromHash(hash);
  if (fromHash !== '') return fromHash;
  const rest = stripBase(base, pathname);
  return rest === '' ? '/' : rest;
}

/**
 * 应用内 path → 写进 history 的地址。带 pathname 前缀是为了**丢掉 query**
 * （见文件头）；根路径下结果形如 `/#/chat/dm/101`。
 */
export function hashUrlFor(base: string, path: string): string {
  const prefix = base === '' ? '/' : base;
  return `${prefix}#${path}`;
}
